import { g, Tool } from '@hcie/core';
import { isPointInSelection, buildRectSelection, buildEllipseSelection, buildCircleSelection, buildSingleRowSelection, buildSingleColumnSelection } from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';

export class ShapeSelectionHandler {
    static handleMouseDown(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        const isShapeSelect = [
            (Tool as any).RectSelect.id, (Tool as any).EllipseSelect.id, (Tool as any).CircleSelect.id,
            (Tool as any).SingleRowSelect.id, (Tool as any).SingleColumnSelect.id
        ].includes(id);

        if (!isShapeSelect) return false;

        // Backup current selection to avoid smeared trails
        if (g.selectionCanvas) {
            const bc = document.createElement('canvas');
            bc.width = g.image_width; bc.height = g.image_height;
            const bctx = bc.getContext('2d');
            if (bctx) bctx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            (g as any)._selectionBaseCanvas = bc;
        }

        if (g.isSelectionActive && (g as any).settings?.selection?.ghostMode && (g as any)._selectionEditBounds) {
            const b = (g as any)._selectionEditBounds;
            const { x1, y1, x2, y2 } = b;
            const isCircle = id === (Tool as any).CircleSelect.id;
            
            const handles = isCircle ? [
                { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
                { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
                { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 },
                { x: (x1 + x2) / 2, y: (y1 + y2) / 2 }
            ] : [
                { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
                { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
                { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 }
            ];
            
            const s = 12 / g.zoom;
            let hIdx = -1;
            for (let i = 0; i < handles.length; i++) {
                if (Math.abs(g.pX - handles[i].x) < s && Math.abs(g.pY - handles[i].y) < s) {
                    hIdx = i; break;
                }
            }

            if (hIdx !== -1) {
                if (isCircle && hIdx === 8) {
                    g.movingSelection = true;
                    g.startX = g.pX; g.startY = g.pY;
                } else {
                    (g as any)._draggingCornerIndex = hIdx;
                    // Logic: Set startX/Y to the OPPOSITE point of the dragged handle
                    const oppositesRect = [3, 2, 1, 0, 5, 4, 7, 6];
                    const oppIdx = oppositesRect[hIdx];
                    if (oppIdx !== undefined && handles[oppIdx]) {
                        g.startX = handles[oppIdx].x;
                        g.startY = handles[oppIdx].y;
                    }
                    g.drawing = true;
                }
                return true;
            } else if (typeof (window as any).commitDrawing === 'function') {
                (window as any).commitDrawing();
            }
        }

        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            g.movingSelection = true;
            g.startX = g.pX; g.startY = g.pY;
        } else {
            g.movingSelection = false;
            g.drawing = true;
            // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] Capture shift/alt mode at mouseDown for use during move
            const newMode: SelectionMode = e.shiftKey ? 'add' : (e.altKey ? 'subtract' : 'replace');
            (g as any)._lastSelectionMode = newMode;
        }
        return true;
    }

    static handleMouseMove(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        const isShapeSelect = [
            (Tool as any).RectSelect.id, (Tool as any).EllipseSelect.id, (Tool as any).CircleSelect.id,
            (Tool as any).SingleRowSelect.id, (Tool as any).SingleColumnSelect.id
        ].includes(id);

        if (!isShapeSelect) return false;

        // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] State Collision fix: separate handle drag from g.drawing
        const isDraggingHandle = (g as any)._draggingCornerIndex >= 0;
        if (!g.drawing && !g.movingSelection && !isDraggingHandle) return false;

        // Mode is already locked in at mouseDown; just read it consistently
        const mode: SelectionMode = ((g as any)._lastSelectionMode || 'replace') as SelectionMode;

        if (g.movingSelection) {
            const dx = Math.round(g.pX - g.startX);
            const dy = Math.round(g.pY - g.startY);
            if (typeof (window as any).moveSelectionMask === 'function') {
                (window as any).moveSelectionMask(dx, dy);
                if ((g as any)._selectionEditBounds) {
                    const b = (g as any)._selectionEditBounds;
                    b.x1 += dx; b.x2 += dx; b.y1 += dy; b.y2 += dy;
                }
            }
            g.startX = g.pX; g.startY = g.pY;
            return true;
        }

        const hIdx = (g as any)._draggingCornerIndex;
        let x1 = g.startX, y1 = g.startY, x2 = g.pX, y2 = g.pY;

        // Constraint logic for mid-edge handles (requires _selectionEditBounds)
        const editBounds = (g as any)._selectionEditBounds;
        if (editBounds) {
            if (hIdx === 4 || hIdx === 5) { x1 = editBounds.x1; x2 = editBounds.x2; }
            if (hIdx === 6 || hIdx === 7) { y1 = editBounds.y1; y2 = editBounds.y2; }
        }

        if (id === (Tool as any).RectSelect.id) buildRectSelection(x1, y1, x2, y2, mode);
        else if (id === (Tool as any).EllipseSelect.id) buildEllipseSelection(x1, y1, x2, y2, mode);
        else if (id === (Tool as any).CircleSelect.id) buildCircleSelection(x1, y1, x2, y2, mode);
        else if (id === (Tool as any).SingleRowSelect.id) buildSingleRowSelection(g.pY, mode);
        else if (id === (Tool as any).SingleColumnSelect.id) buildSingleColumnSelection(g.pX, mode);

        // Keep ghost-mode edit bounds in sync during handle drag so overlay handles update in real time.
        if ((g as any).settings?.selection?.ghostMode && (g as any)._draggingCornerIndex >= 0) {
            const minX = Math.min(x1, x2);
            const minY = Math.min(y1, y2);
            const maxX = Math.max(x1, x2);
            const maxY = Math.max(y1, y2);
            (g as any)._selectionEditBounds = {
                // Snap to integer pixels to match mask/border generation and avoid cursor/handle drift.
                x1: Math.floor(minX),
                y1: Math.floor(minY),
                x2: Math.ceil(maxX),
                y2: Math.ceil(maxY),
            };
        }

        return true;
    }

    static handleMouseUp(e: MouseEvent): boolean {
        const id = g.current_tool.id;
        const isShapeSelect = [
            (Tool as any).RectSelect.id, (Tool as any).EllipseSelect.id, (Tool as any).CircleSelect.id,
            (Tool as any).SingleRowSelect.id, (Tool as any).SingleColumnSelect.id
        ].includes(id);

        if (!isShapeSelect || (!g.drawing && !g.movingSelection)) return false;

        if (g.movingSelection) {
            g.movingSelection = false;
        } else {
            // Mode was locked at mouseDown; fallback to e.shiftKey only if mode was never set
            const mode = ((g as any)._lastSelectionMode || (e.shiftKey ? 'add' : (e.altKey ? 'subtract' : 'replace'))) as SelectionMode;
            let x1 = g.startX, y1 = g.startY, x2 = g.pX, y2 = g.pY;
            const hIdx = (g as any)._draggingCornerIndex;
            const eb = (g as any)._selectionEditBounds;

            // Mid-edge handle constraint (null-guarded)
            if (eb) {
                if (hIdx === 4 || hIdx === 5) { x1 = eb.x1; x2 = eb.x2; }
                if (hIdx === 6 || hIdx === 7) { y1 = eb.y1; y2 = eb.y2; }
            }

            // [DEBUG 2026-04-07][TaskID: GDS-Stabilize] CircleSelect always square; RectSelect is NOT forced square by Shift (Shift = add mode)
            if (id === (Tool as any).CircleSelect.id) {
                const dx = x2 - x1, dy = y2 - y1;
                const side = Math.max(Math.abs(dx), Math.abs(dy));
                x2 = x1 + (dx < 0 ? -side : side);
                y2 = y1 + (dy < 0 ? -side : side);
            }

            if ((g as any).settings?.selection?.ghostMode) {
                const minX = Math.min(x1, x2);
                const minY = Math.min(y1, y2);
                const maxX = Math.max(x1, x2);
                const maxY = Math.max(y1, y2);
                (g as any)._selectionEditBounds = { x1: Math.floor(minX), y1: Math.floor(minY), x2: Math.ceil(maxX), y2: Math.ceil(maxY) };
                (g as any)._lastSelectionMode = mode;
                (g as any)._draggingCornerIndex = -1;
            }
            g.drawing = false;
        }
        return true;
    }

    static getHoverCursor(): string | null {
        if (!g.isSelectionActive || !(g as any)._selectionEditBounds) return null;
        
        const { x1, y1, x2, y2 } = (g as any)._selectionEditBounds;
        const cursors = ['nwse-resize', 'nesw-resize', 'nesw-resize', 'nwse-resize', 'ns-resize', 'ns-resize', 'ew-resize', 'ew-resize', 'move'];
        const handles = [
            { x: x1, y: y1 }, { x: x2, y: y1 }, { x: x1, y: y2 }, { x: x2, y: y2 },
            { x: (x1 + x2) / 2, y: y1 }, { x: (x1 + x2) / 2, y: y2 },
            { x: x1, y: (y1 + y2) / 2 }, { x: x2, y: (y1 + y2) / 2 },
            { x: (x1 + x2) / 2, y: (y1 + y2) / 2 } // Center move handle for Circle
        ];

        const s = 12 / g.zoom;
        for (let i = 0; i < handles.length; i++) {
            if (Math.abs(g.pX - handles[i].x) < s && Math.abs(g.pY - handles[i].y) < s) {
                return cursors[i] || 'pointer';
            }
        }

        if (typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            return 'move';
        }

        return null;
    }
}
